import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { traceSpan } from '@/lib/sentry-spans';
import type { Session, User as SupabaseUser } from '@supabase/supabase-js';

interface AuthUser {
  id: string;
  email: string;
  role: string;
  name: string;
}

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  isAdmin: boolean;
  isSuperadmin: boolean;
  isLoading: boolean;
  isRecovery: boolean;
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isAdmin: false,
  isSuperadmin: false,
  isLoading: true,
  isRecovery: false,
  login: async () => null,
  logout: async () => {},
});

/** Map Supabase auth user to our AuthUser by looking up the public.users table */
async function resolveAuthUser(supaUser: SupabaseUser): Promise<AuthUser | null> {
  const { data, error } = await supabase
    .from('users')
    .select('id, email, role, full_name')
    .eq('email', supaUser.email!)
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    email: data.email,
    role: data.role ?? 'employee',
    name: data.full_name,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRecovery, setIsRecovery] = useState(false);
  const initialized = useRef(false);
  const isRecoveryRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Prevent double-initialization in React Strict Mode
    if (initialized.current) return;
    initialized.current = true;

    let mounted = true;

    /**
     * IMPORTANT: Never call supabase.from() inside an onAuthStateChange callback.
     * The Supabase v2 auth client holds an internal queue lock during the callback,
     * which causes any database query to deadlock indefinitely.
     * Workaround: defer all DB work via setTimeout(0) so it runs after the callback returns.
     */
    const handleSession = (s: Session | null) => {
      if (!mounted) return;
      setSession(s);
      // Sync Realtime-klient med auth-token eksplisitt. Supabase JS skal
      // normalt gjøre dette automatisk, men er race-følsomt ved oppstart
      // av nye WebSocket-forbindelser (CHANNEL_ERROR ved sub uten token).
      if (s?.access_token) {
        supabase.realtime.setAuth(s.access_token);
      }
      if (!s?.user) {
        setUser(null);
        setIsLoading(false);
        return;
      }
      // Defer DB call to avoid auth-callback deadlock
      setTimeout(async () => {
        if (!mounted) return;
        try {
          const resolved = await resolveAuthUser(s.user);
          if (mounted) setUser(resolved);
        } finally {
          if (mounted) setIsLoading(false);
        }
      }, 0);
    };

    // 1. Check existing session immediately (handles "already logged in" case)
    supabase.auth.getSession().then(({ data: { session: s } }) => handleSession(s));

    // 2. Subscribe to future auth changes (login/logout/token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      // VIB-634: Supabase sender PASSWORD_RECOVERY når brukeren lander på
      // appen via reset-mail-lenken. Vi setter recovery-state og navigerer
      // til reset-skjemaet før vi prosesserer sessionen videre. Recovery-
      // sessionen er midlertidig og lar oss kalle supabase.auth.updateUser
      // med nytt passord, men brukeren skal ikke ende opp på dashboard
      // før de faktisk har valgt et nytt passord.
      if (event === 'PASSWORD_RECOVERY') {
        isRecoveryRef.current = true;
        setIsRecovery(true);
        handleSession(s);
        navigate('/reset-password', { replace: true });
        return;
      }

      // Når brukeren har lagret nytt passord får vi USER_UPDATED. Hvis vi
      // sto i recovery-flyten redirecter vi til dashboard og clearer flagget.
      if (event === 'USER_UPDATED' && isRecoveryRef.current) {
        isRecoveryRef.current = false;
        setIsRecovery(false);
        handleSession(s);
        navigate('/', { replace: true });
        return;
      }

      handleSession(s);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<string | null> => {
    // VIB-642: instrumenter login så Sentry Performance viser p95-latency
    // og hvor mange forsøk som feiler. Span-navnet er statisk så det
    // grupperer på tvers av brukere.
    const { error } = await traceSpan("auth.signIn", "auth", () =>
      supabase.auth.signInWithPassword({ email, password }),
    );
    if (error) return error.message;
    return null;
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem("solvrev_impersonation_user_id");
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      session,
      isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
      isSuperadmin: user?.role === 'superadmin',
      isLoading,
      isRecovery,
      login,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
